import React, { useCallback, useState } from 'react';
import { usePresupuesto } from '@/hooks/usePresupuesto';
import { usePresupuestosPermissions } from '@/hooks/usePresupuestosPermissions';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { SeccionDocumentacion } from '@/components/Presupuestos/SeccionDocumentacion';
import { SeccionPlanDeCostos } from '@/components/Costos/SeccionPlanDeCostos';
import { presupuestoService } from '@/services/presupuestoService';
import { usePDFViewer } from '@/contexts/PDFViewerContext';
import { ResizableSplitPanel } from '@/components/ResizableSplitPanel/ResizableSplitPanel';
import { PDFViewer } from '@/components/PDFViewer/PDFViewer';
import { useIsMobile } from '@/hooks/use-mobile';

export interface PresupuestosProps {
  tramite: any;
}

const Presupuestos: React.FC<PresupuestosProps> = ({ tramite }) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const canSendWhatsApp = checkAllPermissions(userPermissions, [PERMISSIONS.SEND_WHATSAPP.name]);
  const permissions = usePresupuestosPermissions();
  const { isOpen, pdfUrl, documentoNombre, closeViewer } = usePDFViewer();
  const [splitContainer, setSplitContainer] = useState<HTMLDivElement | null>(null);
  const setSplitLeftPanelRef = useCallback((node: HTMLDivElement | null) => {
    setSplitContainer(node);
  }, []);
  const isMobile = useIsMobile();
  const isPdfOpen = isOpen && !!pdfUrl;
  // En celulares no entran dos paneles: el PDF se muestra a pantalla completa.
  const isSplitView = isPdfOpen && !isMobile;

  const {
    presupuesto,
    isLoading,
    cargarDocumento,
    actualizarDocumento,
    eliminarDocumento,
    isCargandoDocumento,
    isActualizandoDocumento,
    isEliminandoDocumento,
  } = usePresupuesto(tramite.id);

  const handleDescargarDocumento = async (documentoId: string) => {
    try {
      const blob = await presupuestoService.descargarDocumento(tramite.id, documentoId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const documento = presupuesto?.documentos.find((d) => d.id === documentoId);
      a.download = documento?.nombreCustom || documento?.nombre || 'documento.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error al descargar documento:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const anyLoadingDoc = isCargandoDocumento || isActualizandoDocumento || isEliminandoDocumento;

  const formContent = (
    <>
      {/* Items de Presupuesto */}
      <div>
        <SeccionPlanDeCostos
          tramiteId={tramite.id}
          tramite={tramite}
          isSplitView={isSplitView}
          splitContainer={splitContainer}
        />
      </div>

      {/* Sección de documentación */}
      <div className="mt-6">
        <SeccionDocumentacion
          tramiteId={tramite.id}
          tramite={tramite}
          documentos={presupuesto?.documentos || []}
          onCargarDocumento={(tipo, nombreCustom, file) =>
            cargarDocumento({ tipo, nombreCustom, file })
          }
          onActualizarDocumento={(documentoId, nombreCustom, tipo) =>
            actualizarDocumento({ documentoId, nombreCustom, tipo })
          }
          onEliminarDocumento={(documentoId) => eliminarDocumento(documentoId)}
          onDescargarDocumento={handleDescargarDocumento}
          isLoading={anyLoadingDoc}
          canUpload={permissions.canCargarDocumento}
          canEdit={permissions.canCargarDocumento}
          canDelete={permissions.canEliminarDocumento}
          canDownload={permissions.canDescargarDocumento}
          canSendWhatsApp={canSendWhatsApp}
        />
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {isSplitView ? (
        <ResizableSplitPanel
          leftPanel={
            <div ref={setSplitLeftPanelRef} className="h-full overflow-auto p-4">
              {formContent}
            </div>
          }
          rightPanel={
            <PDFViewer
              pdfUrl={pdfUrl}
              documentoNombre={documentoNombre || 'Documento'}
              onClose={closeViewer}
            />
          }
        />
      ) : (
        formContent
      )}
      {isPdfOpen && isMobile && (
        <div className="fixed inset-0 z-50 h-[100dvh] bg-white">
          <PDFViewer
            pdfUrl={pdfUrl}
            documentoNombre={documentoNombre || 'Documento'}
            onClose={closeViewer}
          />
        </div>
      )}
    </div>
  );
};

export default Presupuestos;
